<script setup>
import Modal from './Modal.vue';
</script>
<template>
    <modal @close="$emit('close')" class="timetable-diff-modal">
        <template #header>
            <h3>Compare timetable exports</h3>
        </template>
        <template #body>
            <div class="timetable-diff">
                <div class="timetable-diff-dates">
                    <span class="timetable-diff-date older">{{ formatDate(olderDate) }}</span>
                    <span class="timetable-diff-arrow">→</span>
                    <span class="timetable-diff-date newer">{{ formatDate(newerDate) }}</span>
                </div>

                <div v-if="diff.identical" class="timetable-diff-identical">
                    No differences.
                </div>

                <div v-else>
                    <div class="timetable-diff-summary">
                        <span v-for="section in changedSections" :key="section.key" class="timetable-diff-chip">
                            {{ section.title }}
                            <b v-if="section.added.length" class="added">+{{ section.added.length }}</b>
                            <b v-if="section.removed.length" class="removed">−{{ section.removed.length }}</b>
                            <b v-if="section.changed.length" class="changed">~{{ section.changed.length }}</b>
                        </span>
                    </div>

                    <div v-for="section in changedSections" :key="section.key" class="timetable-diff-section">
                        <div class="timetable-diff-section-header" @click="toggle(section.key)">
                            <span class="timetable-diff-caret">{{ isOpen(section.key) ? '▾' : '▸' }}</span>
                            <span class="timetable-diff-section-title">{{ section.title }}</span>
                            <span class="timetable-diff-counts">
                                <span v-if="section.added.length" class="added">+{{ section.added.length }}</span>
                                <span v-if="section.removed.length" class="removed">−{{ section.removed.length }}</span>
                                <span v-if="section.changed.length" class="changed">~{{ section.changed.length }}</span>
                            </span>
                        </div>

                        <div v-if="isOpen(section.key)" class="timetable-diff-section-body">
                            <div v-if="section.added.length" class="timetable-diff-bucket">
                                <div class="timetable-diff-bucket-title added">Added</div>
                                <ul>
                                    <li v-for="item in capped(section.added)" :key="item.key">{{ item.label }}</li>
                                </ul>
                                <div v-if="section.added.length > cap" class="timetable-diff-more">
                                    …and {{ section.added.length - cap }} more
                                </div>
                            </div>

                            <div v-if="section.removed.length" class="timetable-diff-bucket">
                                <div class="timetable-diff-bucket-title removed">Removed</div>
                                <ul>
                                    <li v-for="item in capped(section.removed)" :key="item.key">{{ item.label }}</li>
                                </ul>
                                <div v-if="section.removed.length > cap" class="timetable-diff-more">
                                    …and {{ section.removed.length - cap }} more
                                </div>
                            </div>

                            <div v-if="section.changed.length" class="timetable-diff-bucket">
                                <div class="timetable-diff-bucket-title changed">Changed</div>
                                <ul>
                                    <li v-for="item in capped(section.changed)" :key="item.key"
                                        class="timetable-diff-changed">
                                        <div class="timetable-diff-changed-label">{{ item.label }}</div>
                                        <ul class="timetable-diff-fields">
                                            <li v-for="change in item.changes" :key="change.field">
                                                <span class="timetable-diff-field">{{ change.field }}</span>
                                                <template v-if="isSetChange(change)">
                                                    <span v-if="change.added.length" class="added">+{{
                                                        change.added.join(', ') }}</span>
                                                    <span v-if="change.removed.length" class="removed">−{{
                                                        change.removed.join(', ') }}</span>
                                                </template>
                                                <template v-else>
                                                    <span class="removed">{{ formatValue(change.from) }}</span>
                                                    <span class="timetable-diff-arrow">→</span>
                                                    <span class="added">{{ formatValue(change.to) }}</span>
                                                </template>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                                <div v-if="section.changed.length > cap" class="timetable-diff-more">
                                    …and {{ section.changed.length - cap }} more
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</template>
<script>
import moment from 'moment';
import { diffTimetables } from '@/timetableDiff.js';

export default {
    props: {
        older: { type: Object, required: true },
        newer: { type: Object, required: true },
        olderDate: { type: String, default: null },
        newerDate: { type: String, default: null },
    },
    emits: ['close'],
    data() {
        return {
            openSections: {},
            cap: 200,
        };
    },
    computed: {
        diff() {
            return diffTimetables(this.older, this.newer);
        },
        changedSections() {
            return this.diff.sections.filter(section => section.total > 0);
        },
    },
    methods: {
        formatDate(date) {
            if (!date)
                return 'Unknown';
            return moment(date).format('ddd D MMM YYYY h:mm A');
        },
        formatValue(value) {
            if (value === null || typeof value === 'undefined')
                return '(none)';
            if (value === '')
                return '(empty)';
            if (typeof value === 'object')
                return JSON.stringify(value);
            return String(value);
        },
        isSetChange(change) {
            return Array.isArray(change.added) || Array.isArray(change.removed);
        },
        toggle(key) {
            this.openSections[key] = !this.isOpen(key);
        },
        isOpen(key) {
            // Sections start expanded; toggling can collapse them.
            return this.openSections[key] !== false;
        },
        capped(list) {
            return list.slice(0, this.cap);
        },
    },
};
</script>
<style>
.timetable-diff-modal .modal-container {
    max-width: 760px;
    text-align: left;
}

.timetable-diff {
    font-size: 0.95em;
}

.timetable-diff-dates {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.timetable-diff-date {
    padding: 3px 10px;
    border-radius: 12px;
    background: #eee;
    font-weight: bold;
}

.timetable-diff-date.newer {
    background: #5252bb;
    color: white;
}

.timetable-diff-arrow {
    color: #888;
    margin: 0 2px;
}

.timetable-diff-identical {
    text-align: center;
    padding: 24px;
    font-size: 1.1em;
}

.timetable-diff-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
}

.timetable-diff-chip {
    background: #f2f2f7;
    border-radius: 12px;
    padding: 3px 10px;
    font-size: 0.9em;
}

.timetable-diff-chip b {
    margin-left: 4px;
}

.timetable-diff .added {
    color: #1a7f37;
}

.timetable-diff .removed {
    color: #b3261e;
}

.timetable-diff .changed {
    color: #8a6d00;
}

.timetable-diff-section {
    border: solid 1px #e2e2e8;
    border-radius: 8px;
    margin-bottom: 8px;
    overflow: hidden;
}

.timetable-diff-section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #f7f7fb;
    cursor: pointer;
    user-select: none;
}

.timetable-diff-section-header:hover {
    background: #eeeef6;
}

.timetable-diff-caret {
    color: #888;
    width: 12px;
}

.timetable-diff-section-title {
    font-weight: bold;
    flex: 1;
}

.timetable-diff-counts {
    display: flex;
    gap: 10px;
    font-weight: bold;
}

.timetable-diff-section-body {
    padding: 4px 12px 12px;
}

.timetable-diff-bucket {
    margin-top: 10px;
}

.timetable-diff-bucket-title {
    font-weight: bold;
    text-transform: uppercase;
    font-size: 0.75em;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
}

.timetable-diff-bucket ul {
    margin: 0;
    padding-left: 20px;
}

.timetable-diff-changed {
    margin-bottom: 6px;
}

.timetable-diff-changed-label {
    font-weight: bold;
}

.timetable-diff-fields {
    list-style: none;
    padding-left: 0 !important;
    margin: 2px 0 0 !important;
}

.timetable-diff-fields > li {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px;
    font-size: 0.9em;
    padding: 1px 0;
}

.timetable-diff-field {
    font-family: monospace;
    background: #f2f2f7;
    border-radius: 4px;
    padding: 0 5px;
    color: #444;
}

.timetable-diff-more {
    color: #888;
    font-style: italic;
    font-size: 0.85em;
    margin-top: 2px;
}
</style>
